import { useState } from "react";

import { AreaChart, Bot, CheckCircle, Clock, FileCode, PencilRuler, UserCheck } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import LoadingSpinner from "./LoadingSpinner";

const AiOptimizationGuide = () => {
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const handleVerify = () => {
    setVerifying(true);
    setTimeout(() => {
      setVerifying(false);
      setVerified(true);
    }, 3000);
  };

  // Icon color utility (since Tailwind's dynamic colors like text-${bot.color}-500 don't work reliably)
  const getIconColor = (color) => {
    switch (color) {
      case "green":
        return "text-green-600";
      case "orange":
        return "text-orange-600";
      case "blue":
        return "text-blue-600";
      case "purple":
        return "text-purple-600";
      case "cyan":
        return "text-cyan-600";
      case "red":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const crawlers = [
    // ... (data unchanged)
    {
      name: "ChatGPT-User",
      service: "OpenAI / ChatGPT",
      purpose: "Fetches URLs on-demand when a user asks a question requiring up-to-date info.",
      color: "green",
    },
    {
      name: "ClaudeBot",
      service: "Anthropic / Claude",
      purpose:
        "Retrieves cited URLs during chat sessions to answer user questions with current information.",
      color: "orange",
    },
    {
      name: "OAI-SearchBot",
      service: "OpenAI",
      purpose: "Indexes pages for ChatGPT's research and live references (RAG).",
      color: "blue",
    },
    {
      name: "Perplexity-User",
      service: "Perplexity AI",
      purpose: "Loads a page when a user clicks a citation, treated as human-like traffic.",
      color: "purple",
    },
    {
      name: "YouBot",
      service: "You.com",
      purpose:
        "Indexes search results to allow their AI Assistant to answer questions with inline sources.",
      color: "cyan",
    },
    {
      name: "Google-Extended",
      service: "Google / Gemini",
      purpose: "Used for Gemini and other AI-related indexing beyond standard search.",
      color: "red",
    },
  ];

  const playbookSteps = [
    {
      icon: <FileCode className={`w-8 h-8 ${getIconColor("blue")}`} />,
      title: "Allowlist AI Crawlers",
      description:
        "Open the gates to AI crawlers by explicitly allowing them in your robots.txt file. If you block them, you won't get indexed and won't appear in AI-driven answers.",
      details: "Add the following lines to your public `robots.txt` file:",
      code: "User-agent: GPTBot\nDisallow:\n\nUser-agent: OAI-SearchBot\nDisallow:\n\nUser-agent: ClaudeBot\nDisallow:\n\nUser-agent: Google-Extended\nDisallow:",
    },
    {
      icon: <AreaChart className={`w-8 h-8 ${getIconColor("green")}`} />,
      title: "Track AI Referral Traffic",
      description:
        "These bots self-report in analytics tools like Google Analytics. Monitor this new traffic source to measure which optimizations improve your visibility in AI search results.",
      details:
        "Look for traffic with specific UTM parameters that identify them as AI referrals. Establish a baseline now and track your growth over time.",
    },
    {
      icon: <PencilRuler className={`w-8 h-8 ${getIconColor("orange")}`} />,
      title: "Reframe Content for AI Search",
      description:
        "Shift from narrative, blog-style writing to a structured Q&A format. AI crawlers are looking for direct, clear answers to specific questions.",
      details:
        "Use clear headings, bullet points, and build content around user questions (FAQs, comparisons, guides). See the example below.",
    },
    {
      icon: <UserCheck className={`w-8 h-8 ${getIconColor("purple")}`} />,
      title: "Maintain Human Value",
      description:
        "While optimizing for AI, ensure your content still builds trust and converts once a human clicks through. The goal is to get discovered by AI and win over the human visitor.",
      details:
        "Balance structured data with engaging copy, compelling images, and clear calls-to-action. An AI might find you, but a human makes the booking.",
    },
  ];

  // Data for Correlation Chart (unchanged)
  const correlationData = [
    { name: "Prompt Injection", value: 0.18 },
    { name: "Content Freshness", value: 0.35 },
    { name: "Rapid Indexing", value: 0.42 },
    { name: "Customer Reviews", value: 0.45 },
    { name: "Knowledge Graph", value: 0.5 },
    { name: "User-Generated Content", value: 0.52 },
    { name: "E-E-A-T & Credibility", value: 0.6 },
    { name: "Brand Mentions & PR", value: 0.72 },
    { name: "Comprehensive Content", value: 0.78 },
    { name: "Structured Content", value: 0.82 },
  ].sort((a, b) => a.value - b.value);

  // Chart colors
  const chartBarColor = "#f97316"; // Orange - Brand color
  const chartBgColor = "#e2e7ff"; // surface-container-high
  const axisColor = "#434656"; // on-surface-variant

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="text-center p-6 bg-surface-container-lowest rounded-3xl shadow-cloud">
        <h2 className="text-3xl font-bold text-on-surface">Guide to LLM Optimization</h2>
        <p className="mt-2 text-on-surface-variant max-w-2xl mx-auto">
          Welcome to the future of search. This brief guide provides actionable steps to make your
          hotel website a prime destination for AI crawlers and the users who rely on them.
        </p>
      </div>

      {/* Install Tracking Section */}
      <div className="p-6 bg-surface-container-lowest rounded-3xl shadow-cloud relative">
        <div className="flex flex-col md:flex-row items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-on-surface mb-4 text-center md:text-left">
            Install with one line of code
          </h3>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-sm text-on-surface-variant">
              <Clock className="w-4 h-4 text-on-surface-variant" />
              Setup takes 10 min on average
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-primary/10 text-primary">
              Recommended
            </span>
          </div>
        </div>

        <div className="border-t pt-6 border-surface-container-high">
          <p className="text-on-surface-variant mb-2 font-semibold">
            Get it custom developed with us for a minimal charge, and low effort.
          </p>

          <div className="mt-6">
            <h4 className="text-xl font-bold text-on-surface mb-2">
              Connect Google Analytics to track everything
            </h4>

            <div className="flex items-start gap-2">
              {!verifying && !verified && (
                <button
                  onClick={handleVerify}
                  className="px-4 py-2 text-sm font-semibold text-on-primary bg-gradient-to-br from-primary to-primary-container rounded-xl hover:opacity-90 transition-colors"
                >
                  Connect Google Analytics Coming Soon!
                </button>
              )}
              {verifying && (
                <div className="flex items-center gap-2 text-sm text-yellow-500">
                  <LoadingSpinner size="sm" message="Loading optimization data..." />
                </div>
              )}
              {verified && (
                <div className="flex items-center gap-2 text-sm text-green-500 font-semibold">
                  <CheckCircle className="w-5 h-5" />
                  Comming Soon!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Correlation Chart Section */}
      <div className="p-6 bg-surface-container-lowest rounded-3xl shadow-cloud">
        <h3 className="text-xl font-bold text-on-surface mb-4 text-center">
          Correlation of LLM Optimization Strategies
        </h3>
        <p className="text-center text-sm text-on-surface-variant mb-6">
          This chart shows the (simulated) correlation between optimization tactics and LLM search
          visibility.
        </p>
        <div style={{ width: "100%", height: 400 }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={correlationData}
              layout="vertical"
              margin={{ top: 20, right: 180, left: 180, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e7ff" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 0.9]}
                ticks={[0, 0.25, 0.5, 0.75]}
                stroke={axisColor}
                tick={{ fill: axisColor, fontSize: 12 }}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={170}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: axisColor }}
              />
              <RechartsTooltip
                cursor={{
                  fill: "rgba(249, 115, 22, 0.15)",
                }}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e2e7ff",
                  borderRadius: 4,
                }}
                formatter={(value) => [`${(value * 100).toFixed(0)}%`, "Correlation"]}
                labelFormatter={() => ""}
              />
              <Bar
                dataKey="value"
                fill={chartBarColor}
                background={{ fill: chartBgColor, radius: 4 }}
                radius={4}
              >
                {correlationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chartBarColor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Crawlers Section */}
      <div className="p-6 bg-surface-container-lowest rounded-3xl shadow-cloud">
        <h3 className="text-xl font-bold text-on-surface mb-4 text-center">
          Who's Knocking? Meet the AI Crawlers
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {crawlers.map((bot) => (
            <div
              key={bot.name}
              className="p-4 bg-surface-container-low rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center gap-3 mb-2">
                <Bot className={`w-6 h-6 ${getIconColor(bot.color)}`} />
                <div>
                  <h4 className="font-semibold text-on-surface">{bot.name}</h4>
                  <p className="text-xs text-on-surface-variant">{bot.service}</p>
                </div>
              </div>
              <p className="text-sm text-on-surface-variant">{bot.purpose}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Playbook Section */}
      <div className="p-6 bg-surface-container-lowest rounded-3xl shadow-cloud">
        <h3 className="text-xl font-bold text-on-surface mb-4 text-center">
          Your 4-Step Playbook for LLM Success
        </h3>
        <div className="space-y-6">
          {playbookSteps.map((step, index) => (
            <div
              key={step.title}
              className="flex items-start gap-6 p-4 bg-surface-container-low rounded-xl"
            >
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center border shadow-sm bg-surface-container-lowest border-surface-container-high">
                  {step.icon}
                </div>
                <span className="mt-2 text-xs font-bold text-on-surface-variant">
                  STEP {index + 1}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-on-surface">{step.title}</h4>
                <p className="text-on-surface-variant mt-1">{step.description}</p>
                <p className="text-sm text-on-surface-variant mt-3">{step.details}</p>
                {step.code && (
                  <pre className="mt-2 p-3 bg-surface-container-high rounded-lg text-xs font-mono overflow-x-auto">
                    <code className="text-on-surface">{step.code}</code>
                  </pre>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Transformation */}
      <div className="p-4 bg-surface-container-low rounded-xl">
        <h3 className="text-xl font-bold text-on-surface mb-4 text-center">
          Content Transformation: Before & After
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg p-4 border border-red-200 bg-white">
            <h4 className="font-bold text-red-600 mb-2">Before: Narrative Style</h4>
            <p className="text-sm text-on-surface-variant italic">
              "Welcome to the Grand Plaza, where luxury meets comfort. After a long day of exploring
              downtown, our guests love to unwind by our stunning rooftop pool, which offers
              breathtaking city views. We also have a state-of-the-art gym and a serene spa for
              complete relaxation. For your convenience, we have several parking options
              available..."
            </p>
          </div>
          <div className="rounded-lg p-4 border border-green-200 bg-white">
            <h4 className="font-bold text-green-600 mb-2">After: Structured Q&A</h4>
            <div className="text-sm text-on-surface-variant space-y-2">
              <p>
                <strong>Q: What amenities does the Grand Plaza have?</strong>
              </p>
              <p>A: The Grand Plaza offers a rooftop pool, a 24/7 gym, and a full-service spa.</p>
              <p>
                <strong>Q: Is there parking at the Grand Plaza?</strong>
              </p>
              <p>A: Yes, we offer valet and self-parking options for a daily fee.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-br from-primary to-primary-container text-white p-8 rounded-xl text-center">
        <h3 className="text-2xl font-bold">The Bottom Line</h3>
        <p className="mt-2 max-w-3xl mx-auto opacity-90">
          Open the gates to AI crawlers, track AI referrals as a new traffic source, and reshape
          your content into direct Q&A formats. This will maximize your visibility in the new
          landscape of LLM search results.
        </p>
      </div>
    </div>
  );
};

export default AiOptimizationGuide;
